// SPDX-License-Identifier: AGPL-3.0-or-later

import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import type {SelectOption} from '@app/features/ui/components/form/FormSelect';
import styles from '@app/features/ui/components/form/SelectBottomSheet.module.css';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CaretDownIcon, CheckIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import React, {useId, useMemo, useState} from 'react';
import type {FilterOptionOption} from 'react-select';

const SELECT_DESCRIPTOR = msg({
	message: 'Select…',
	comment: 'Generic select control placeholder.',
});
const SELECTED_DESCRIPTOR = msg({
	message: '{selectedCount} selected',
	comment: 'Generic multi-select summary label. selectedCount is the number of selected options.',
});
const SEARCH_DESCRIPTOR = msg({
	message: 'Search',
	comment: 'Form placeholder or label for a search input.',
});
const NO_RESULTS_FOUND_DESCRIPTOR = msg({
	message: 'No results found',
	comment: 'Empty state shown when a search returns no matches.',
});

type Primitive = string | number | null;

interface SelectBottomSheetProps<
	V extends Primitive = string,
	IsMulti extends boolean = false,
	O extends SelectOption<V> = SelectOption<V>,
> {
	label?: React.ReactNode;
	description?: string;
	value: IsMulti extends true ? Array<V> : V;
	options: ReadonlyArray<O>;
	onChange: (value: IsMulti extends true ? Array<V> : V) => void;
	disabled?: boolean;
	error?: string;
	placeholder?: string;
	className?: string;
	id?: string;
	isSearchable?: boolean;
	filterOption?: (option: FilterOptionOption<O>, inputValue: string) => boolean;
	isMulti?: IsMulti;
	renderOption?: (option: O, isSelected: boolean) => React.ReactNode;
	renderValue?: (option: IsMulti extends true ? Array<O> : O | null) => React.ReactNode;
	'aria-label'?: string;
}

interface OptionItemProps<V extends Primitive, O extends SelectOption<V> = SelectOption<V>> {
	option: O;
	isSelected: boolean;
	onSelect: () => void;
	customContent?: React.ReactNode;
}

const OptionItem = <V extends Primitive, O extends SelectOption<V> = SelectOption<V>>({
	option,
	isSelected,
	onSelect,
	customContent,
}: OptionItemProps<V, O>) => (
	<button
		type="button"
		onClick={onSelect}
		className={clsx(styles.optionButton, isSelected && styles.optionButtonSelected)}
		disabled={option.isDisabled}
		aria-pressed={isSelected}
		data-flx="ui.form.select-bottom-sheet.option-item.option-button.select"
	>
		{customContent ?? (
			<span
				className={clsx(styles.optionLabel, option.isDisabled && styles.optionDisabled)}
				data-flx="ui.form.select-bottom-sheet.option-item.option-label"
			>
				{option.label}
			</span>
		)}
		{isSelected && (
			<div
				className={styles.checkIconContainer}
				data-flx="ui.form.select-bottom-sheet.option-item.check-icon-container"
			>
				<CheckIcon
					weight="bold"
					className={styles.checkIcon}
					data-flx="ui.form.select-bottom-sheet.option-item.check-icon"
				/>
			</div>
		)}
	</button>
);
export const SelectBottomSheet = observer(function SelectBottomSheet<
	V extends Primitive = string,
	IsMulti extends boolean = false,
	O extends SelectOption<V> = SelectOption<V>,
>({
	id,
	label,
	description,
	value,
	options,
	onChange,
	disabled = false,
	error,
	placeholder,
	className,
	isSearchable = false,
	filterOption,
	isMulti,
	renderOption,
	renderValue,
	'aria-label': ariaLabel,
}: SelectBottomSheetProps<V, IsMulti, O>) {
	const {i18n} = useLingui();
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const selectedOption = useMemo(() => {
		if (isMulti) {
			if (!Array.isArray(value)) return [];
			const valueSet = new Set(value as Array<V>);
			return options.filter((option) => valueSet.has(option.value));
		}
		return options.find((option) => option.value === value) || null;
	}, [isMulti, options, value]);
	const displayValue = useMemo(() => {
		if (renderValue) {
			return renderValue(selectedOption as IsMulti extends true ? Array<O> : O | null);
		}
		if (isMulti) {
			const selected = selectedOption as Array<O>;
			if (selected.length === 0) return placeholder ?? i18n._(SELECT_DESCRIPTOR);
			if (selected.length === 1) return selected[0].label;
			return i18n._(SELECTED_DESCRIPTOR, {selectedCount: selected.length});
		}
		return (selectedOption as O | null)?.label ?? placeholder ?? i18n._(SELECT_DESCRIPTOR);
	}, [isMulti, selectedOption, placeholder, renderValue, i18n]);
	const filteredOptions = useMemo(() => {
		const query = searchQuery.trim();
		if (!isSearchable || query.length === 0) return options;
		const loweredQuery = query.toLowerCase();
		return options.filter((option) => {
			if (filterOption) {
				return filterOption(
					{
						label: option.label,
						value: option.value == null ? '' : String(option.value),
						data: option,
					},
					query,
				);
			}
			return option.label.toLowerCase().includes(loweredQuery);
		});
	}, [filterOption, isSearchable, options, searchQuery]);
	const handleOpen = () => {
		if (!disabled) {
			setSearchQuery('');
			setIsOpen(true);
		}
	};
	const handleClose = () => {
		setSearchQuery('');
		setIsOpen(false);
	};
	const handleSelect = (optionValue: V) => {
		if (isMulti) {
			const currentValues = Array.isArray(value) ? (value as Array<V>) : [];
			const newValues = currentValues.includes(optionValue)
				? currentValues.filter((v) => v !== optionValue)
				: [...currentValues, optionValue];
			(onChange as (value: Array<V>) => void)(newValues);
		} else {
			(onChange as (value: V) => void)(optionValue);
			handleClose();
		}
	};
	const isOptionSelected = (optionValue: V): boolean => {
		if (isMulti) {
			return Array.isArray(value) && (value as Array<V>).includes(optionValue);
		}
		return value === optionValue;
	};
	const generatedTriggerId = useId();
	const triggerId = id ?? generatedTriggerId;
	return (
		<div className={styles.container} data-flx="ui.form.select-bottom-sheet.container">
			{label && (
				<label
					htmlFor={triggerId}
					className={clsx(styles.label, disabled && styles.disabled)}
					data-flx="ui.form.select-bottom-sheet.label"
				>
					{label}
				</label>
			)}
			<div className={className} data-flx="ui.form.select-bottom-sheet.div">
				<button
					id={triggerId}
					type="button"
					onClick={handleOpen}
					disabled={disabled}
					className={clsx(styles.trigger, disabled && styles.triggerDisabled, error && styles.triggerError)}
					aria-label={ariaLabel}
					data-flx="ui.form.select-bottom-sheet.trigger.open.button"
				>
					<span
						className={clsx(styles.triggerValue, !selectedOption && styles.triggerPlaceholder)}
						data-flx="ui.form.select-bottom-sheet.trigger-value"
					>
						{displayValue}
					</span>
					<CaretDownIcon
						weight="bold"
						className={styles.triggerIcon}
						data-flx="ui.form.select-bottom-sheet.trigger-icon"
					/>
				</button>
			</div>
			{description && (
				<p
					className={clsx(styles.description, disabled && styles.disabled)}
					data-flx="ui.form.select-bottom-sheet.description"
				>
					{description}
				</p>
			)}
			{error && (
				<span className={styles.errorText} data-flx="ui.form.select-bottom-sheet.error-text">
					{error}
				</span>
			)}
			<BottomSheet
				isOpen={isOpen}
				onClose={handleClose}
				snapPoints={[0, 0.6, 1]}
				initialSnap={1}
				disableDefaultHeader
				zIndex={30000}
				data-flx="ui.form.select-bottom-sheet.bottom-sheet"
			>
				<div className={styles.scrollContainer} data-flx="ui.form.select-bottom-sheet.scroll-container">
					{isSearchable && (
						<div className={styles.searchWrapper} data-flx="ui.form.select-bottom-sheet.search-wrapper">
							<input
								type="search"
								className={styles.searchInput}
								placeholder={placeholder ?? i18n._(SEARCH_DESCRIPTOR)}
								value={searchQuery}
								onChange={(event) => setSearchQuery(event.target.value)}
								aria-label={ariaLabel ?? placeholder ?? i18n._(SEARCH_DESCRIPTOR)}
								data-flx="ui.form.select-bottom-sheet.search-input.set-search-query"
							/>
						</div>
					)}
					<div className={styles.optionsContainer} data-flx="ui.form.select-bottom-sheet.options-container">
						{filteredOptions.length === 0 ? (
							<div className={styles.emptyState} data-flx="ui.form.select-bottom-sheet.empty-state">
								{i18n._(NO_RESULTS_FOUND_DESCRIPTOR)}
							</div>
						) : null}
						{filteredOptions.map((option, index) => {
							const isSelected = isOptionSelected(option.value);
							return (
								<React.Fragment key={String(option.value)}>
									<OptionItem
										option={option}
										isSelected={isSelected}
										onSelect={() => handleSelect(option.value)}
										customContent={renderOption?.(option, isSelected)}
										data-flx="ui.form.select-bottom-sheet.option-item.select"
									/>
									{index < filteredOptions.length - 1 && (
										<div className={styles.divider} data-flx="ui.form.select-bottom-sheet.divider" />
									)}
								</React.Fragment>
							);
						})}
					</div>
					<div className={styles.bottomSpacer} data-flx="ui.form.select-bottom-sheet.bottom-spacer" />
				</div>
			</BottomSheet>
		</div>
	);
});
